import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Select } from "@/shared/ui/components";
import { useGeoLocations } from "../../hooks";
import type {
  SupportedLanguage,
  LocalizedName,
  Prefecture,
  City,
} from "../../types";

export type LocationType = "prefecture" | "city" | "ward";

export interface LocationItem {
  id: string;
  name: LocalizedName;
  code?: number;
  city_slug?: string;
}

export interface LocationDropdownProps {
  type: LocationType;
  value: string;
  onChange: (item: LocationItem | null) => void;
  prefectureCode?: number;
  citySlug?: string;
  lang: SupportedLanguage;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Finds the matching item value, supporting both code/slug and name matching.
 * This allows the dropdown to work with both:
 * - Direct values (code "13" or city_slug "shibuya")
 * - Name values ("Tokyo" or "東京都") for backward compatibility with existing data
 */
function findMatchingValue(
  currentValue: string,
  sourceData: (Prefecture | City)[],
  type: LocationType,
  _lang: SupportedLanguage,
): string {
  if (!currentValue) return "";

  // First try direct match (code or city_slug)
  for (const item of sourceData) {
    const itemValue =
      type === "prefecture"
        ? String((item as Prefecture).code)
        : (item as City).city_slug;
    if (itemValue === currentValue) {
      return itemValue;
    }
  }

  // Fall back to name matching (for edit mode where names are stored)
  for (const item of sourceData) {
    // Match by any locale name
    if (
      item.name.ja === currentValue ||
      item.name.en === currentValue ||
      item.name.ru === currentValue
    ) {
      return type === "prefecture"
        ? String((item as Prefecture).code)
        : (item as City).city_slug;
    }
  }

  return currentValue; // Return as-is if no match found
}

export const LocationDropdown = observer((props: LocationDropdownProps) => {
  const {
    type,
    value,
    onChange,
    lang,
    label,
    placeholder,
    required,
    disabled,
    prefectureCode,
  } = props;

  const { prefectures, cities, loading } = useGeoLocations({
    lang,
    prefectureCode,
  });

  const options = useMemo(() => {
    const sourceData = type === "prefecture" ? prefectures : cities;
    return sourceData.map((item) => ({
      value:
        type === "prefecture"
          ? String((item as Prefecture).code)
          : (item as City).city_slug,
      label: item.name[lang],
    }));
  }, [prefectures, cities, type, lang]);

  // Resolve the current value to the correct format
  const resolvedValue = useMemo(() => {
    const sourceData = type === "prefecture" ? prefectures : cities;
    return findMatchingValue(value, sourceData, type, lang);
  }, [value, prefectures, cities, type, lang]);

  const handleChange = (selectedValue: string | number) => {
    const valueAsString = String(selectedValue);
    const sourceData = type === "prefecture" ? prefectures : cities;
    const selectedItem = sourceData.find((item) =>
      type === "prefecture"
        ? String((item as Prefecture).code) === valueAsString
        : (item as City).city_slug === valueAsString,
    );

    if (selectedItem) {
      const result: LocationItem = {
        id: selectedItem.id,
        name: selectedItem.name,
      };
      if (type === "prefecture") {
        result.code = (selectedItem as Prefecture).code;
      } else {
        result.city_slug = (selectedItem as City).city_slug;
      }
      onChange(result);
    } else {
      onChange(null);
    }
  };

  return (
    <Select
      label={label}
      placeholder={placeholder}
      options={options}
      value={resolvedValue}
      onChange={handleChange}
      required={required}
      disabled={disabled || loading}
    />
  );
});
